/**
 * 經典文獻研讀平台 - 核心JavaScript
 * 純前端實現，無需後端服務器
 */

class ClassicLiteraturePlatform {
  constructor() {
    this.library = null;
    this.currentClassic = null;
    this.currentVersion = null;
    this.currentChapter = null;
    this.selectedAnnotations = new Set(); // 支持多選，动态设置
    this.displayMode = 'comparison'; // 'comparison' or 'single'
    this.currentSingleAnnotationType = null; // 單一模式下當前顯示的注本，动态设置
    this.userData = this.loadUserData();
    this.classicsManager = null; // 将在初始化时设置
  }

  /**
   * 初始化應用
   */
  async init() {
    try {
      // 等待经典管理器加载
      this.classicsManager = window.classicsManager;
      
      await this.loadLibrary();
      this.setupEventListeners();
      this.initReaderMode();
    } catch (error) {
      console.error('初始化失敗:', error);
      this.showError('應用初始化失敗，請刷新頁面重試');
    }
  }

  /**
   * 載入文獻庫數據
   */
  async loadLibrary() {
    try {
      // 默认加载道德经
      this.library = await this.classicsManager.loadClassic('tao-te-ching');
      
      // 設置默認文獻
      if (this.library.classics && this.library.classics.length > 0) {
        this.currentClassic = this.library.classics[0];
        if (this.currentClassic.versions && this.currentClassic.versions.length > 0) {
          this.currentVersion = this.currentClassic.versions[0];
          if (this.currentVersion.chapters && this.currentVersion.chapters.length > 0) {
            this.currentChapter = this.currentVersion.chapters[0];
            // 设置默认注本选择
            this.setDefaultAnnotations();
          }
        }
      }
    } catch (error) {
      console.error('載入文獻數據失敗:', error);
      // 如果加载失败，尝试使用备用数据
      if (window.TAO_TE_CHING_DATA) {
        this.library = window.TAO_TE_CHING_DATA;
        this.setupDefaultData();
      } else if (window.LIBRARY_DATA) {
        // 兼容旧的变量名
        this.library = window.LIBRARY_DATA;
        this.setupDefaultData();
      } else {
        throw error;
      }
    }
  }

  /**
   * 设置默认数据
   */
  setupDefaultData() {
    if (this.library.classics && this.library.classics.length > 0) {
      this.currentClassic = this.library.classics[0];
      if (this.currentClassic.versions && this.currentClassic.versions.length > 0) {
        this.currentVersion = this.currentClassic.versions[0];
        if (this.currentVersion.chapters && this.currentVersion.chapters.length > 0) {
          this.currentChapter = this.currentVersion.chapters[0];
          // 设置默认选中的注本（选择第一个可用的注本）
          this.setDefaultAnnotations();
        }
      }
    }
  }

  /**
   * 设置默认注本选择
   */
  setDefaultAnnotations() {
    if (this.currentChapter && this.currentChapter.annotations) {
      const availableAnnotations = Object.keys(this.currentChapter.annotations);
      if (availableAnnotations.length > 0) {
        // 默认选择第一个注本
        const firstAnnotation = availableAnnotations[0];
        this.selectedAnnotations.clear();
        this.selectedAnnotations.add(firstAnnotation);
        this.currentSingleAnnotationType = firstAnnotation;
      }
    }
  }



  /**
   * 載入用戶數據
   */
  loadUserData() {
    const data = localStorage.getItem('classicLiteratureUserData');
    if (data) {
      return JSON.parse(data);
    }
    return {
      notes: {},
      bookmarks: [],
      readingProgress: {},
      preferences: {
        fontSize: 18,
        theme: 'light'
      }
    };
  }

  /**
   * 保存用戶數據
   */
  saveUserData() {
    localStorage.setItem('classicLiteratureUserData', JSON.stringify(this.userData));
  }

  /**
   * 設置事件監聽器
   */
  setupEventListeners() {
    // 版本切換
    const versionSelect = document.getElementById('version-select');
    if (versionSelect) {
      versionSelect.addEventListener('change', async (e) => {
        const value = e.target.value;
        
        if (value.startsWith('classic:')) {
          // 切换经典
          const classicId = value.replace('classic:', '');
          await this.switchClassic(classicId);
        } else if (value.startsWith('version:')) {
          // 切换版本
          const versionId = value.replace('version:', '');
          this.switchVersion(versionId);
        }
      });
    }

    // 搜索功能
    const searchInput = document.getElementById('search-input');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.handleSearch(e.target.value);
      });
    }

    // 移动端菜单控制
    this.setupMobileMenu();
  }

  /**
   * 设置移动端菜单
   */
  setupMobileMenu() {
    const menuBtn = document.getElementById('mobile-menu-btn');
    const sidebar = document.getElementById('sidebar');
    const overlay = document.getElementById('mobile-overlay');

    if (menuBtn && sidebar && overlay) {
      // 菜单按钮点击
      menuBtn.addEventListener('click', () => {
        sidebar.classList.add('show');
        overlay.classList.add('show');
      });

      // 遮罩层点击关闭
      overlay.addEventListener('click', () => {
        sidebar.classList.remove('show');
        overlay.classList.remove('show');
      });

      // 章节点击后自动关闭菜单（移动端）
      const originalLoadChapter = this.loadChapter.bind(this);
      this.loadChapter = (chapterId) => {
        originalLoadChapter(chapterId);
        // 在移动端关闭侧边栏
        if (window.innerWidth <= 768) {
          sidebar.classList.remove('show');
          overlay.classList.remove('show');
        }
      };
    }
  }

  /**
   * 初始化閱讀模式
   */
  initReaderMode() {
    this.renderChapterList();
    this.renderVersionSelect();
    this.renderCurrentChapter();
    this.restoreReadingProgress();
  }

  /**
   * 渲染章節列表
   */
  renderChapterList() {
    const container = document.getElementById('chapter-list');
    if (!container || !this.currentVersion) return;

    container.innerHTML = '';
    this.currentVersion.chapters.forEach(chapter => {
      const item = document.createElement('div');
      item.className = 'chapter-item';
      item.textContent = chapter.title;
      item.dataset.chapterId = chapter.id;
      
      if (chapter.id === this.currentChapter.id) {
        item.classList.add('active');
      }

      item.addEventListener('click', () => {
        this.loadChapter(chapter.id);
      });

      container.appendChild(item);
    });
  }

  /**
   * 渲染版本選擇器
   */
  renderVersionSelect() {
    const select = document.getElementById('version-select');
    if (!select) return;

    select.innerHTML = '';

    // 获取可用经典列表
    const availableClassics = this.classicsManager ? this.classicsManager.getAvailableClassics() : [];
    
    // 只有多个经典时才显示经典选择组
    if (availableClassics.length > 1) {
      const classicOptGroup = document.createElement('optgroup');
      classicOptGroup.label = '选择经典';
      
      availableClassics.forEach(classic => {
        const option = document.createElement('option');
        option.value = `classic:${classic.id}`;
        option.textContent = `${classic.title} (${classic.author})`;
        if (this.currentClassic && this.currentClassic.id === classic.id) {
          option.selected = true;
        }
        classicOptGroup.appendChild(option);
      });
      
      select.appendChild(classicOptGroup);
    }

    // 添加版本选择选项
    if (this.currentClassic && this.currentClassic.versions) {
      // 如果只有一个经典，直接显示版本，不用分组
      if (availableClassics.length === 1) {
        this.currentClassic.versions.forEach(version => {
          const option = document.createElement('option');
          option.value = `version:${version.id}`;
          option.textContent = `${this.currentClassic.title} - ${version.name}`;
          if (this.currentVersion && version.id === this.currentVersion.id) {
            option.selected = true;
          }
          select.appendChild(option);
        });
      } else {
        // 多个经典时使用分组
        const versionOptGroup = document.createElement('optgroup');
        versionOptGroup.label = '选择版本';
        
        this.currentClassic.versions.forEach(version => {
          const option = document.createElement('option');
          option.value = `version:${version.id}`;
          option.textContent = version.name;
          if (this.currentVersion && version.id === this.currentVersion.id) {
            option.selected = true;
          }
          versionOptGroup.appendChild(option);
        });
        
        select.appendChild(versionOptGroup);
      }
    }

    // 如果没有任何选项，显示提示
    if (select.children.length === 0) {
      const option = document.createElement('option');
      option.textContent = '暂无可用版本';
      option.disabled = true;
      select.appendChild(option);
    }
  }

  /**
   * 渲染當前章節
   */
  renderCurrentChapter() {
    const container = document.getElementById('reading-area');
    if (!container || !this.currentChapter) return;

    // 章節標題 - 显示为"经典名 章节标题"
    const title = document.createElement('h2');
    title.className = 'chapter-title';
    const classicTitle = this.currentClassic ? this.currentClassic.title : '';
    title.textContent = `${classicTitle} ${this.currentChapter.title}`;

    // 章節內容容器
    const content = document.createElement('div');
    content.className = 'chapter-content';

    // 控制面板
    const controlPanel = this.createControlPanel();
    content.appendChild(controlPanel);

    // 原文顯示區
    const originalTextSection = this.createOriginalTextSection();
    content.appendChild(originalTextSection);

    // 對比顯示區
    const comparisonArea = this.createComparisonArea();
    content.appendChild(comparisonArea);

    container.innerHTML = '';
    container.appendChild(title);
    container.appendChild(content);

    // 初始化顯示
    this.updateDisplay();
  }

  /**
   * 創建控制面板
   */
  createControlPanel() {
    const panel = document.createElement('div');
    panel.className = 'control-panel';

    // 標題
    const title = document.createElement('h3');
    title.innerHTML = '⚙️ 顯示設置';
    panel.appendChild(title);

    // 顯示模式選擇
    const modeSection = document.createElement('div');
    modeSection.className = 'display-mode';

    // 對比模式
    const comparisonMode = document.createElement('div');
    comparisonMode.className = 'mode-option active';
    
    const comparisonRadio = document.createElement('input');
    comparisonRadio.type = 'radio';
    comparisonRadio.name = 'display-mode';
    comparisonRadio.value = 'comparison';
    comparisonRadio.checked = true;
    comparisonRadio.id = 'mode-comparison';
    
    const comparisonLabel = document.createElement('label');
    comparisonLabel.htmlFor = 'mode-comparison';
    comparisonLabel.textContent = '對比模式';
    
    comparisonMode.appendChild(comparisonRadio);
    comparisonMode.appendChild(comparisonLabel);
    
    comparisonMode.addEventListener('click', () => {
      this.displayMode = 'comparison';
      comparisonRadio.checked = true;
      this.updateModeSelection();
      this.updateDisplay();
    });

    // 單一模式
    const singleMode = document.createElement('div');
    singleMode.className = 'mode-option';
    
    const singleRadio = document.createElement('input');
    singleRadio.type = 'radio';
    singleRadio.name = 'display-mode';
    singleRadio.value = 'single';
    singleRadio.id = 'mode-single';
    
    const singleLabel = document.createElement('label');
    singleLabel.htmlFor = 'mode-single';
    singleLabel.textContent = '單一模式';
    
    singleMode.appendChild(singleRadio);
    singleMode.appendChild(singleLabel);
    
    singleMode.addEventListener('click', () => {
      this.displayMode = 'single';
      singleRadio.checked = true;
      this.updateModeSelection();
      this.updateDisplay();
    });

    modeSection.appendChild(comparisonMode);
    modeSection.appendChild(singleMode);
    panel.appendChild(modeSection);

    // 模式說明
    const modeDescription = document.createElement('div');
    modeDescription.style.cssText = `
      margin: 1rem 0;
      padding: 0.75rem;
      background: #f0f9ff;
      border-left: 3px solid #4299e1;
      border-radius: 4px;
      font-size: 0.85rem;
      color: var(--secondary-color);
    `;
    modeDescription.id = 'mode-description';
    this.updateModeDescription(modeDescription);
    panel.appendChild(modeDescription);

    // 注本選擇器
    const selector = this.createAnnotationSelector();
    panel.appendChild(selector);

    return panel;
  }

  /**
   * 更新模式說明
   */
  updateModeDescription(element) {
    if (this.displayMode === 'comparison') {
      element.innerHTML = '💡 對比模式：同時顯示所有選中的注本，便於對比不同觀點';
    } else {
      element.innerHTML = '💡 單一模式：一次只顯示一個注本，可通過切換器查看不同注本';
    }
  }

  /**
   * 創建注本選擇器
   */
  createAnnotationSelector() {
    const container = document.createElement('div');
    
    const selectorTitle = document.createElement('h4');
    selectorTitle.textContent = '選擇要顯示的注本：';
    selectorTitle.style.marginBottom = '1rem';
    selectorTitle.style.color = 'var(--primary-color)';
    container.appendChild(selectorTitle);

    const selector = document.createElement('div');
    selector.className = 'annotation-selector';

    // 动态获取当前章节的所有可用注本
    if (this.currentChapter && this.currentChapter.annotations) {
      const availableAnnotations = Object.keys(this.currentChapter.annotations);
      
      availableAnnotations.forEach((type, index) => {
        const option = document.createElement('div');
        const level = index + 1; // 动态分配层级
        option.className = `annotation-option level-${level}`;

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.id = `annotation-${type}`;
        checkbox.value = type;
        checkbox.checked = this.selectedAnnotations.has(type);

        const label = document.createElement('label');
        label.htmlFor = `annotation-${type}`;
        label.textContent = type;

        checkbox.addEventListener('change', () => {
          if (checkbox.checked) {
            this.selectedAnnotations.add(type);
          } else {
            this.selectedAnnotations.delete(type);
          }
          this.updateDisplay();
        });

        option.appendChild(checkbox);
        option.appendChild(label);
        selector.appendChild(option);
      });
    }

    container.appendChild(selector);
    return container;
  }

  /**
   * 創建原文顯示區
   */
  createOriginalTextSection() {
    const section = document.createElement('div');
    section.className = 'original-text-section';
    section.id = 'original-text-section';

    const header = document.createElement('h4');
    header.innerHTML = '📜 原文（無分段）';
    section.appendChild(header);

    const content = document.createElement('div');
    content.className = 'original-text-content';
    content.id = 'original-text-content';
    section.appendChild(content);

    return section;
  }

  /**
   * 創建對比顯示區
   */
  createComparisonArea() {
    const area = document.createElement('div');
    area.className = 'comparison-area';
    area.id = 'comparison-area';

    return area;
  }

  /**
   * 更新顯示
   */
  updateDisplay() {
    // 更新原文顯示
    this.updateOriginalText();
    
    // 更新對比區域
    this.updateComparisonArea();
  }

  /**
   * 更新原文顯示
   */
  updateOriginalText() {
    const content = document.getElementById('original-text-content');
    if (!content || !this.currentChapter) return;

    content.textContent = this.currentChapter.originalText || '原文數據不可用';
  }

  /**
   * 更新對比區域
   */
  updateComparisonArea() {
    const area = document.getElementById('comparison-area');
    if (!area) return;

    area.innerHTML = '';

    const selectedCount = this.selectedAnnotations.size;
    
    if (this.displayMode === 'single') {
      // 單一模式：只顯示第一個選中的注本，但允許切換
      this.renderSingleMode(area);
    } else {
      // 對比模式：同時顯示所有選中的注本
      this.renderComparisonMode(area, selectedCount);
    }

    if (selectedCount === 0) {
      area.innerHTML = '<p style="text-align: center; color: var(--secondary-color); padding: 2rem;">請選擇至少一個注本進行顯示</p>';
    }
  }

  /**
   * 渲染單一模式
   */
  renderSingleMode(area) {
    area.className = 'comparison-area single-column';
    
    // 創建注本切換器
    const switcher = this.createAnnotationSwitcher();
    area.appendChild(switcher);
    
    // 顯示當前選中的注本
    const currentType = this.getCurrentAnnotationType();
    if (currentType) {
      const level = this.getAnnotationLevel(currentType);
      const section = this.createAnnotationSection(currentType, level);
      area.appendChild(section);
    }
  }

  /**
   * 渲染對比模式
   */
  renderComparisonMode(area, selectedCount) {
    // 設置網格布局
    if (selectedCount === 1) {
      area.className = 'comparison-area single-column';
    } else if (selectedCount === 2) {
      area.className = 'comparison-area two-columns';
    } else if (selectedCount === 3) {
      area.className = 'comparison-area three-columns';
    } else if (selectedCount >= 4) {
      area.className = 'comparison-area four-columns';
    }

    // 为每个选中的注本创建显示区域
    if (this.currentChapter && this.currentChapter.annotations) {
      const availableAnnotations = Object.keys(this.currentChapter.annotations);
      let levelIndex = 1;
      
      availableAnnotations.forEach((type) => {
        if (this.selectedAnnotations.has(type)) {
          const section = this.createAnnotationSection(type, levelIndex);
          area.appendChild(section);
          levelIndex++;
        }
      });
    }
  }

  /**
   * 創建注本切換器（單一模式用）
   */
  createAnnotationSwitcher() {
    const switcher = document.createElement('div');
    switcher.className = 'annotation-switcher';
    switcher.style.cssText = `
      display: flex;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
      justify-content: center;
      flex-wrap: wrap;
    `;

    // 动态获取选中的注本
    if (this.currentChapter && this.currentChapter.annotations) {
      const availableAnnotations = Object.keys(this.currentChapter.annotations);
      let levelIndex = 1;
      
      availableAnnotations.forEach((type) => {
        if (this.selectedAnnotations.has(type)) {
          const button = document.createElement('button');
          button.className = `annotation-switch-btn level-${levelIndex}`;
          button.textContent = type;
          button.style.cssText = `
            padding: 0.5rem 1rem;
            border: 2px solid var(--border-color);
            border-radius: 20px;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 0.9rem;
            background: white;
          `;

          if (type === this.getCurrentAnnotationType()) {
            button.style.background = this.getAnnotationColor(levelIndex);
            button.style.color = 'white';
            button.style.borderColor = this.getAnnotationColor(levelIndex);
          }

          button.addEventListener('click', () => {
            this.setCurrentAnnotationType(type);
            this.updateDisplay();
          });

          switcher.appendChild(button);
          levelIndex++;
        }
      });
    }

    return switcher;
  }

  /**
   * 獲取當前注釋類型
   */
  getCurrentAnnotationType() {
    if (this.currentSingleAnnotationType && this.selectedAnnotations.has(this.currentSingleAnnotationType)) {
      return this.currentSingleAnnotationType;
    }
    // 返回第一個選中的注本
    return Array.from(this.selectedAnnotations)[0];
  }

  /**
   * 設置當前注釋類型
   */
  setCurrentAnnotationType(type) {
    this.currentSingleAnnotationType = type;
  }

  /**
   * 獲取注釋層級（动态分配）
   */
  getAnnotationLevel(type) {
    if (this.currentChapter && this.currentChapter.annotations) {
      const availableAnnotations = Object.keys(this.currentChapter.annotations);
      const index = availableAnnotations.indexOf(type);
      return index !== -1 ? index + 1 : 1;
    }
    return 1;
  }

  /**
   * 獲取注釋顏色
   */
  getAnnotationColor(level) {
    const colors = {
      1: '#4299e1',
      2: '#48bb78',
      3: '#ed8936',
      4: '#9f7aea'
    };
    return colors[level] || '#4299e1';
  }

  /**
   * 創建注釋區域
   */
  createAnnotationSection(annotationType, level) {
    const section = document.createElement('div');
    section.className = `annotation-section level-${level}`;

    // 標題
    const header = document.createElement('h4');
    const icons = ['📘', '📗', '📙', '📕', '📖', '📚', '📜', '📋']; // 扩展图标数组
    const iconIndex = Math.min(level - 1, icons.length - 1);
    header.innerHTML = `${icons[iconIndex]} ${annotationType}`;
    section.appendChild(header);

    // 分句顯示
    const segmentsDisplay = document.createElement('div');
    segmentsDisplay.className = 'segments-display';

    const annotation = this.currentChapter.annotations[annotationType];
    if (annotation && annotation.segments) {
      annotation.segments.forEach((segment, index) => {
        const segmentItem = document.createElement('div');
        segmentItem.className = 'segment-item';
        segmentItem.dataset.segmentId = segment.id;

        const segmentIndex = document.createElement('span');
        segmentIndex.className = 'segment-index';
        segmentIndex.textContent = index + 1;

        const segmentText = document.createElement('div');
        segmentText.className = 'segment-text';
        segmentText.textContent = segment.text;

        const segmentNote = document.createElement('div');
        segmentNote.className = 'segment-note';
        segmentNote.textContent = segment.note;

        segmentItem.appendChild(segmentIndex);
        segmentItem.appendChild(segmentText);
        segmentItem.appendChild(segmentNote);

        // 點擊高亮
        segmentItem.addEventListener('click', () => {
          // 移除其他高亮
          segmentsDisplay.querySelectorAll('.segment-item').forEach(item => {
            item.classList.remove('active');
          });
          segmentItem.classList.add('active');
        });

        segmentsDisplay.appendChild(segmentItem);
      });
    }

    section.appendChild(segmentsDisplay);
    return section;
  }

  /**
   * 更新模式選擇
   */
  updateModeSelection() {
    const modeOptions = document.querySelectorAll('.mode-option');
    modeOptions.forEach(option => {
      option.classList.remove('active');
      const radio = option.querySelector('input[type="radio"]');
      if (radio && radio.value === this.displayMode) {
        option.classList.add('active');
        radio.checked = true;
      } else if (radio) {
        radio.checked = false;
      }
    });

    // 更新模式說明
    const modeDescription = document.getElementById('mode-description');
    if (modeDescription) {
      this.updateModeDescription(modeDescription);
    }
  }





  /**
   * 載入章節
   */
  loadChapter(chapterId) {
    const chapter = this.currentVersion.chapters.find(ch => ch.id === chapterId);
    if (chapter) {
      this.currentChapter = chapter;
      // 重新设置默认注本选择
      this.setDefaultAnnotations();
      this.renderCurrentChapter();
      this.renderChapterList();
      this.saveReadingProgress();
    }
  }

  /**
   * 切换经典
   */
  async switchClassic(classicId) {
    try {
      // 加载新的经典数据
      this.library = await this.classicsManager.loadClassic(classicId);
      
      // 设置新的默认数据
      this.setupDefaultData();
      
      // 重新渲染界面
      this.renderVersionSelect();
      this.renderChapterList();
      this.renderCurrentChapter();
      
      console.log(`已切换到经典: ${classicId}`);
    } catch (error) {
      console.error('切换经典失败:', error);
      this.showError(`切换经典失败: ${error.message}`);
    }
  }

  /**
   * 切換版本
   */
  switchVersion(versionId) {
    const version = this.currentClassic.versions.find(v => v.id === versionId);
    if (version) {
      this.currentVersion = version;
      if (version.chapters && version.chapters.length > 0) {
        this.currentChapter = version.chapters[0];
        // 重新设置默认注本选择
        this.setDefaultAnnotations();
      }
      this.renderChapterList();
      this.renderCurrentChapter();
    }
  }

  /**
   * 搜索功能
   */
  handleSearch(query) {
    if (!query.trim()) {
      this.updateDisplay();
      return;
    }

    // 在原文中高亮
    const originalTextContent = document.getElementById('original-text-content');
    if (originalTextContent) {
      const text = this.currentChapter.originalText || '';
      if (text.includes(query)) {
        originalTextContent.innerHTML = text.replace(new RegExp(query, 'g'), `<mark>${query}</mark>`);
      }
    }

    // 在注釋中高亮
    const segmentItems = document.querySelectorAll('.segment-item');
    segmentItems.forEach(item => {
      const textDiv = item.querySelector('.segment-text');
      const noteDiv = item.querySelector('.segment-note');
      
      if (textDiv && textDiv.textContent.includes(query)) {
        textDiv.innerHTML = textDiv.textContent.replace(new RegExp(query, 'g'), `<mark>${query}</mark>`);
      }
      
      if (noteDiv && noteDiv.textContent.includes(query)) {
        noteDiv.innerHTML = noteDiv.textContent.replace(new RegExp(query, 'g'), `<mark>${query}</mark>`);
      }
    });
  }



  /**
   * 保存閱讀進度
   */
  saveReadingProgress() {
    if (this.currentChapter) {
      this.userData.readingProgress[this.currentClassic.id] = {
        versionId: this.currentVersion.id,
        chapterId: this.currentChapter.id,
        timestamp: Date.now()
      };
      this.saveUserData();
    }
  }

  /**
   * 恢復閱讀進度
   */
  restoreReadingProgress() {
    const progress = this.userData.readingProgress[this.currentClassic.id];
    if (progress) {
      this.switchVersion(progress.versionId);
      this.loadChapter(progress.chapterId);
    }
  }

  /**
   * 顯示錯誤信息
   */
  showError(message) {
    alert(message);
  }



  /**
   * 導出數據為JSON
   */
  exportData() {
    const dataStr = JSON.stringify(this.library, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `library_${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  }
}

// 全局實例
let platform = null;

// 頁面載入完成後初始化
document.addEventListener('DOMContentLoaded', () => {
  platform = new ClassicLiteraturePlatform();
  platform.init();
});
